import Vue from 'vue'
import Component from 'vue-class-component'
import { Dict } from '@/utils/interfaces'
import { normalizeSnakeCase } from '@/utils/functions'

interface FormatterFunc {
  (response: Dict): Dict
}

export interface APIErrorOptions {
  formatter?: FormatterFunc
  title?: string
}

/** Fallback heading when a caller has not named the failed operation. */
export const DEFAULT_ERROR_TITLE = 'errors.titles.generic'

@Component
export class HandleErrorsMixin extends Vue {
  handleAPIErrors(response: Dict, options?: APIErrorOptions) {
    // If caller provided a formatter, format the response
    // formatted data should come back with a list of flattened strings.
    if (options?.formatter) {
      response = options.formatter(response)
    }

    let messages: string[] = []

    // add an error detail, should be a flat string already
    if (response.data.detail) {
      messages.push(response.data.detail)
    }

    // iterate over and flatten non field errors
    if (response.data.non_field_errors?.length) {
      messages = messages.concat(response.data.non_field_errors)
    }

    // Take a list of objects and flatten it for display
    if (
      !response.data.non_field_errors?.length &&
      typeof response.data === 'object'
    ) {
      const flattened = Object.entries(response.data)
        .filter(([key]) => !['non_field_errors', 'detail'].includes(key))
        .map(([key, value]) => `${normalizeSnakeCase(key)} ${value}`)

      messages = messages.concat(flattened)
    }

    const title = options?.title ?? DEFAULT_ERROR_TITLE

    for (const message of messages) {
      this.toastError(message, title)
    }
  }

  /**
   * Both arguments are translated here, so both are i18n KEYS -- a server
   * string that matches no key passes through unchanged, which is what the
   * `handleAPIErrors` messages rely on. Call sites must NOT pre-translate:
   * `$t` on an already-translated sentence is parsed as a key path and logs a
   * missing-key warning for every toast.
   */
  toastError(message: string, title: string = DEFAULT_ERROR_TITLE) {
    this.$toast.error(this.$t(message) as string, this.$t(title) as string)
  }
}
